import React, { useCallback, useState, useEffect } from 'react'
import { createSelector } from 'reselect'
import classNames from 'classnames'

import { HomeIcon } from 'pharmacy/src/style/logo'
import { Header } from 'pharmacy/src/navigation/header'
import { Link } from 'pharmacy/src/navigation/link'
import { ErrorBoundary } from 'pharmacy/src/misc/errorBoundary'
import { Icon } from 'pharmacy/src/display/icon'

import { getRequest } from 'mednet-cns/src/api/v1'
import {
  FETCH_USER_WITH_PERMISSIONS,
  FETCH_USER_WITH_PERMISSIONS_PUBLIC,
} from 'mednet-cns/src/reducers/user'

import { useSelector } from 'react-redux'

import { publicRoutes } from 'constants/publicRoutes'
import { ROUTES } from 'constants/routes'
import { PAGES } from 'constants/pages'

import useHomePath from '../../hooks/useHomePath'

import HeaderDropdown from './headerDropdown'
import HeaderSearch from './headerSearch'
import { GatedAskQuestionButton } from './headerAskQuestionButton'
import { HeaderRegisterButton } from './headerRegisterButton'
import HeaderLink from './headerLink'

import css from './anteriorHeader.scss'
import { HeaderLlmSearch } from './headerLlmSearch'
import useNewSearchExperienceFlag from './useNewSearchExperienceFlag'

const UPDATED_SEARCH_TOOLTIP_ID = 'updated-search-tooltip'

const AnteriorHeader = (props) => {
  const selectUser = useCallback(
    createSelector(
      (state) => state.user.data,
      (state) => getRequest(state, FETCH_USER_WITH_PERMISSIONS_PUBLIC),
      (state) => getRequest(state, FETCH_USER_WITH_PERMISSIONS),
      (userData, publicUserRequest, userRequest) => {
        return {
          userData,
          isLoading: userRequest.isLoading || publicUserRequest.isLoading,
          isError: userRequest.isError || publicUserRequest.isError,
        }
      }
    )
  )

  const { userData, isLoading, isError } = useSelector(selectUser)
  const isGuest = isLoading || isError || !userData?.userId
  const [isGuestMenuVisible, setGuestMenuVisible] = useState(false)
  const [isLoginPageActive, setLoginPageActive] = useState(
    props.history.location.pathname === ROUTES[PAGES.LOGIN].path
  )

  const { enableNewSearchExperience, isLdReady } = useNewSearchExperienceFlag()
  const { homePath, isHomePathExternal } = useHomePath()

  useEffect(() => {
    if (props.history) {
      const unlistenToLocation = props.history.listen(() => {
        setGuestMenuVisible(false)
        setLoginPageActive(
          props.history.location.pathname === ROUTES[PAGES.LOGIN].path
        )
        return unlistenToLocation
      })
    }

    return () => {}
  }, [])
  const renderHomeButton = () => {
    return (
      <div
        className={classNames(css.homeIconContainer, {
          [css.newSearchExperience]: enableNewSearchExperience,
        })}
      >
        <Link
          external={isLoading || isHomePathExternal} // isLoading is needed to redirect when request is loading but user is logged in
          pathname={homePath}
          className={css.homeIconLink}
        >
          {!enableNewSearchExperience && <HomeIcon className={css.logo} />}
          <p
            className={classNames({
              [css.newSearchExperience]: enableNewSearchExperience,
            })}
          >
            Home
          </p>
        </Link>
      </div>
    )
  }

  return (
    <Header
      className={css.header}
      history={props.history}
      publicRoutes={publicRoutes}
    >
      {(isLdReady || (isGuest && !isLoading)) && (
        <>
          <div className={css.logoContainer}>
            <Link
              external={isLoading || isHomePathExternal} // isLoading is needed to redirect when request is loading but user is logged in
              pathname={isGuest ? '/' : homePath}
              className={css.logoLink}
            >
              <img
                src={`//${process.env.ASSET_HOSTNAME}/images/mednet_logo_transparent.png`}
                alt="Mednet"
                className={css.logo}
              />
            </Link>
          </div>
          {!isGuest && renderHomeButton()}
          {!isLoading && (
            <>
              {!isGuest && (
                <>
                  <div
                    className={classNames(css.search, {
                      [css.llmSearch]: enableNewSearchExperience,
                    })}
                    data-tooltip-id={UPDATED_SEARCH_TOOLTIP_ID}
                    data-tooltip-variant="info"
                  >
                    <ErrorBoundary>
                      {!enableNewSearchExperience && <HeaderSearch />}
                      {enableNewSearchExperience && <HeaderLlmSearch />}
                    </ErrorBoundary>
                  </div>
                </>
              )}

              <HeaderDropdown
                className={classNames(
                  isGuest ? css.guestHeaderDropdown : css.headerDropdown,
                  {
                    [css.hiddenMenuInSmallScreen]:
                      isGuest && !isGuestMenuVisible,
                    [css.newSearchExperience]: enableNewSearchExperience,
                  }
                )}
                isGuest={isGuest}
                userData={userData}
                history={props.history}
              />

              {!enableNewSearchExperience && (
                <div className={css.buttonsContainer}>
                  {isGuest ? (
                    <>
                      <Link pathname={ROUTES[PAGES.LOGIN].path}>
                        <HeaderLink
                          className={classNames(css.signInLink, {
                            [css.activeSignInLink]: isLoginPageActive,
                          })}
                        >
                          Sign In
                        </HeaderLink>
                      </Link>

                      <HeaderRegisterButton
                        pathname={props.history?.location?.pathname}
                      />
                      <Icon
                        className={css.menuBars}
                        icon={
                          isGuestMenuVisible
                            ? ['far', 'times']
                            : ['far', 'bars']
                        }
                        onClick={() => setGuestMenuVisible(!isGuestMenuVisible)}
                      />
                    </>
                  ) : (
                    <GatedAskQuestionButton />
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </Header>
  )
}

export default AnteriorHeader
